import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ChevronRight, Phone, Mail, MapPin } from "lucide-react";
import SEO from "../components/SEO";
import { getLegalDoc, COMPANY_INFO } from "../data/legal";

const SITE_URL = "https://www.clevencleaning.nl";

export default function LegalPage({ slug }) {
  const doc = getLegalDoc(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!doc) {
    return <Navigate to="/" replace />;
  }

  const url = `${SITE_URL}/${doc.slug}`;

  return (
    <>
      <SEO
        title={doc.metaTitle}
        description={doc.metaDescription}
        canonicalUrl={url}
      />

      {/* Header band (dark, so the fixed transparent header stays readable) */}
      <section
        className="relative bg-gradient-to-br from-slate-900 via-sky-900 to-teal-900 pt-32 pb-16 overflow-hidden"
        aria-labelledby="legal-heading"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav
            aria-label="Kruimelpad"
            className="flex items-center gap-2 text-sm text-white/60 mb-6"
          >
            <Link to="/" className="hover:text-white transition-colors" data-testid="legal-breadcrumb-home">
              Home
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white/90">{doc.title}</span>
          </nav>
          <h1
            id="legal-heading"
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            data-testid="legal-title"
          >
            {doc.title}
          </h1>
          <p className="text-white/70">Laatst bijgewerkt: {doc.updated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed mb-12">
            {doc.intro}
          </p>

          <div className="space-y-10">
            {doc.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs?.map((p, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
                {section.items && (
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 leading-relaxed">
                    {section.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Contact block */}
          <div className="mt-14 bg-gradient-to-br from-slate-50 to-sky-50 rounded-3xl p-8 border border-slate-100">
            <h2 className="text-xl font-bold text-slate-800 mb-5">Contactgegevens</h2>
            <p className="text-slate-600 mb-5">
              Heeft u vragen over deze pagina? Neem gerust contact met ons op.
            </p>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-sky-600 flex-shrink-0" />
                {COMPANY_INFO.name} &middot; {COMPANY_INFO.city} &middot; KvK {COMPANY_INFO.kvk}
              </li>
              <li>
                <a
                  href={`tel:${COMPANY_INFO.phoneClean}`}
                  className="flex items-center gap-3 hover:text-sky-600 transition-colors"
                  data-testid="legal-contact-phone"
                >
                  <Phone className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  {COMPANY_INFO.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-center gap-3 hover:text-sky-600 transition-colors"
                  data-testid="legal-contact-email"
                >
                  <Mail className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  {COMPANY_INFO.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
